import {
  HTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useState,
} from 'react';

import { cn } from '../utils/cn';

// Context for tabs
interface TabsContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
}

// Tabs Root
export interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ className, defaultValue, value, onValueChange, children, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue || '');
    const activeTab = value ?? internalValue;

    const setActiveTab = (id: string) => {
      if (value === undefined) {
        setInternalValue(id);
      }
      onValueChange?.(id);
    };

    return (
      <TabsContext.Provider value={{ activeTab, setActiveTab }}>
        <div ref={ref} className={cn('w-full', className)} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  }
);

Tabs.displayName = 'Tabs';

// Tabs List
export interface TabsListProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'pills' | 'underline';
}

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, variant = 'default', onKeyDown, ...props }, ref) => {
    // A11y: strzałki lewo/prawo przenoszą fokus (roving tabindex) i aktywują
    // sąsiednią zakładkę, z zawijaniem na końcach tablisty.
    const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const tabs = Array.from(
          e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)')
        );
        const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
        if (current !== -1 && tabs.length > 1) {
          e.preventDefault();
          const next =
            e.key === 'ArrowRight'
              ? (current + 1) % tabs.length
              : (current - 1 + tabs.length) % tabs.length;
          tabs[next].focus();
          tabs[next].click();
        }
      }
      onKeyDown?.(e);
    };

    return (
      <div
        ref={ref}
        role="tablist"
        onKeyDown={handleKeyDown}
        className={cn(
          'flex',
          {
            'border-b border-[color:var(--ids-border,rgba(255,255,255,0.08))] gap-1':
              variant === 'default',
            'gap-2 p-1 bg-[color:var(--ids-bg-subtle,#0F1626)] border border-[color:var(--ids-border,rgba(255,255,255,0.08))] rounded-[var(--ids-radius-md,10px)]':
              variant === 'pills',
            'border-b-2 border-[color:var(--ids-border,rgba(255,255,255,0.08))]':
              variant === 'underline',
          },
          className
        )}
        {...props}
      />
    );
  }
);

TabsList.displayName = 'TabsList';

// Tab Trigger
export interface TabsTriggerProps extends HTMLAttributes<HTMLButtonElement> {
  value: string;
  disabled?: boolean;
  icon?: ReactNode;
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, value, disabled, icon, children, ...props }, ref) => {
    const { activeTab, setActiveTab } = useTabsContext();
    const isActive = activeTab === value;

    return (
      <button
        ref={ref}
        role="tab"
        type="button"
        // Cel martwej dotąd referencji aria-labelledby z TabsContent.
        id={`trigger-${value}`}
        aria-selected={isActive}
        aria-controls={`panel-${value}`}
        // Roving tabindex: Tab wchodzi na aktywną zakładkę, strzałki (obsługa
        // w TabsList) poruszają się po pozostałych.
        tabIndex={isActive ? 0 : -1}
        disabled={disabled}
        onClick={() => setActiveTab(value)}
        className={cn(
          'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ids-focus,#22D3EE)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          {
            // Active state
            'text-[color:var(--ids-brand,#3B82F6)] border-b-2 border-[color:var(--ids-brand,#3B82F6)] -mb-px':
              isActive,
            // Inactive state
            'text-[color:var(--ids-text-muted,#94A3B8)] hover:text-[color:var(--ids-text,#F8FAFC)] border-b-2 border-transparent -mb-px':
              !isActive,
          },
          className
        )}
        {...props}
      >
        {icon}
        {children}
      </button>
    );
  }
);

TabsTrigger.displayName = 'TabsTrigger';

// Tab Content
export interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  forceMount?: boolean;
}

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, value, forceMount, children, ...props }, ref) => {
    const { activeTab } = useTabsContext();
    const isActive = activeTab === value;

    if (!isActive && !forceMount) return null;

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`panel-${value}`}
        aria-labelledby={`trigger-${value}`}
        hidden={!isActive}
        tabIndex={0}
        className={cn(
          'mt-4 focus:outline-none',
          'animate-in fade-in-0 duration-200',
          !isActive && 'hidden',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TabsContent.displayName = 'TabsContent';
